import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

import uz from "./locales/uz";
import en from "./locales/en";
import ru from "./locales/ru";

export const locales = { uz, en, ru };
const STORAGE_KEY = "portfolio-lang";

const LanguageContext = createContext(null);

function interpolate(str, vars) {
	if (typeof str !== "string" || !vars) return str;
	let out = str;
	Object.entries(vars).forEach(([k, v]) => {
		out = out.split(`{{${k}}}`).join(String(v));
	});
	return out;
}

export function LanguageProvider({ children }) {
	const [locale, setLocaleState] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved && locales[saved]) return saved;
		} catch {
			/* ignore */
		}
		return "uz";
	});

	const setLocale = useCallback((lng) => {
		if (!locales[lng]) return;
		setLocaleState(lng);
		try {
			localStorage.setItem(STORAGE_KEY, lng);
		} catch {
			/* ignore */
		}
	}, []);

	useEffect(() => {
		const map = { uz: "uz", en: "en", ru: "ru" };
		document.documentElement.lang = map[locale] || "uz";
	}, [locale]);

	const value = useMemo(() => {
		const messages = locales[locale];

		function t(key, vars) {
			const parts = key.split(".");
			let cur = messages;
			for (const p of parts) {
				if (cur == null || typeof cur !== "object") return key;
				cur = cur[p];
			}
			if (cur == null) return key;
			return typeof cur === "string" ? interpolate(cur, vars) : cur;
		}

		return { locale, setLocale, t, messages };
	}, [locale, setLocale]);

	return (
		<LanguageContext.Provider value={value}>
			{children}
		</LanguageContext.Provider>
	);
}

export function useLanguage() {
	const ctx = useContext(LanguageContext);
	if (!ctx) {
		throw new Error("useLanguage must be used within LanguageProvider");
	}
	return ctx;
}
